/**
 * **内置主题** —— 开箱能用的几套观感。
 *
 * ## 它们不只是「换个色」
 *
 * 每一套都同时改了三件事，用来证明主题系统真的能改这么多：
 *
 * ```text
 *   palette  强调色 / 分类色 / 端口色 / 严重级
 *   recipes  节点壳、节点头、行、控件、端口、选中态的画法
 *   layout   密度（紧凑 / 常规 / 宽松）
 *   css      少数几套带自定义 CSS —— 通道 ③ 的活样例
 * ```
 *
 * ## ⚠️ 端口色**不能随便配色**
 *
 * `tokens.ts` 里那句话是关键：「端口配色就是**类型约束的视觉表达**」——
 * 如果两个不同端口类型看起来一样，用户就会以为它们能连。
 * 所以每套主题都保持**色族不变**（数值=灰、文本=蓝、布尔=黄、
 * 颜色=黄绿、时间=紫、富文本=橙、集合=青、页面=靛、组件=绿），
 * 只调明度/饱和度。这是主题**不许打破**的一条语义约束，
 * `verify:theme` 里有一条断言守着它。
 *
 * ## 分类色里的「新 id」
 *
 * `document` / `page` / `function` / `component` / `container` / `utility`
 * 是早期分类，`data` / `logic` / `text` / `number` / `color` / `time` /
 * `list` / `convert` 是现在两个包实际声明的。
 * 两批都写全 —— 因为**包是数据**，随时可能冒出新分类，
 * 而没声明的分类会落到 `--np-cat-default`（一个中性色），
 * 不会像以前那样「所有未知分类挤在同一个灰里」。
 */
import type { ThemeDocument } from '../types/theme'

/* ══════════════════════════════════════════════════════════════════
   1. graphite —— 默认深色（现状的精修版）
   ══════════════════════════════════════════════════════════════════ */

const graphite: ThemeDocument = {
  id: 'graphite',
  name: '石墨',
  hint: '默认深色，Blender 观感，最耐看',
  scheme: 'dark',
  palette: {
    accent: '#f0a04b',
    danger: '#e05252',
    sockets: {
      number: '#A1A1A1',
      text: '#7EB6D9',
      boolean: '#CCA640',
      color: '#C7C729',
      datetime: '#B36BE6',
      rich: '#E08A3C',
      list: '#4DB8A8',
      page: '#6E6EC7',
      component: '#5FA85F',
      any: '#8A8A8A',
    },
    categories: {
      document: '#4A5D8A',
      page: '#55698A',
      data: '#5E5E5E',
      function: '#3F6E6E',
      logic: '#3F6E6E',
      boolean: '#7A6A3A',
      text: '#4A6E8A',
      number: '#5A5A6E',
      color: '#6E6E3A',
      time: '#6B4A7A',
      list: '#3F6E6A',
      convert: '#5A4A6E',
      component: '#4F7A4F',
      container: '#6B5A7A',
      utility: '#454545',
    },
    severity: { error: '#e05252', warning: '#d9a441', info: '#5b8bb5', success: '#5fa85f' },
  },
  recipes: {
    shell: 'flat',
    node: 'gradient',
    header: 'solid',
    row: 'plain',
    widget: 'blender',
    socket: 'circle',
    state: 'ring',
    popup: 'solid',
  },
  /*
   * ⚠️ 默认主题（graphite）**不写 `motion`** —— 缺省就是 `normal`。
   * 给默认主题关掉动效是个坏默认：新用户第一眼看到的是"死的"界面。
   */
  layout: { density: 'normal' },
}

/* ══════════════════════════════════════════════════════════════════
   2. midnight —— 深蓝 · 毛玻璃
   ══════════════════════════════════════════════════════════════════ */

const midnight: ThemeDocument = {
  id: 'midnight',
  name: '午夜',
  hint: '深蓝毛玻璃，节点浮起，偏「现代工具」',
  scheme: 'dark',
  palette: {
    accent: '#5aa9ff',
    danger: '#ff5c7a',
    sockets: {
      number: '#9aa8bd',
      text: '#5aa9ff',
      boolean: '#ffc857',
      color: '#b8e986',
      datetime: '#b48aff',
      rich: '#ff9f5a',
      list: '#4fd1c5',
      page: '#7c8cff',
      component: '#6ee7a8',
      any: '#7b8ca6',
    },
    categories: {
      document: '#3d5a99',
      page: '#48679e',
      data: '#4a5568',
      function: '#2f7d8c',
      logic: '#2f7d8c',
      boolean: '#8a7228',
      text: '#3a72a8',
      number: '#4d5a80',
      color: '#6d8a2a',
      time: '#6f4aa8',
      list: '#2f8f86',
      convert: '#5a4a9e',
      component: '#3f8a5f',
      container: '#6a4a8a',
      utility: '#3a4453',
    },
    severity: { error: '#ff5c7a', warning: '#ffb454', info: '#5aa9ff', success: '#3ddc97' },
  },
  tokens: {
    /* 外壳比面板更暗一档 —— 层级靠明度分，不靠描边 */
    'surface-0': '#080b12',
    'surface-bar': '#0d121c',
    'surface-bar-soft': '#0a0e16',
    'surface-2': 'rgba(20, 26, 38, 0.72)',
    'surface-popup': 'rgba(16, 21, 32, 0.86)',
    'border-soft': 'rgba(140, 180, 255, 0.12)',
    'border-strong': 'rgba(140, 180, 255, 0.2)',
  },
  recipes: {
    shell: 'layered',
    node: 'glass',
    header: 'gradient',
    row: 'divided',
    widget: 'soft',
    socket: 'circle',
    state: 'glow',
    popup: 'glass',
  },
  /*
   * 通道 ⑤ 的活样例：**让工具栏浮起来**。
   *
   * ⚠️ `reserveSpace` 默认是 `true` —— 视觉浮起但仍然占位，
   * 所以画布拿到的还是真实剩余空间，`fitView()` 不需要知道任何新东西。
   * 想真覆盖画布就写 `reserveSpace: false`，那时区域会把厚度
   * 写进 `--np-region-inset-*`，画布据此让出内边距。
   */
  layout: {
    density: 'compact',
    /*
     * ⚠️ 原来这里是「让工具栏浮起来」。**工具栏已经删掉**，
     * `RegionKey.toolbar` 也一并删除 —— 这条通道现在没有消费者。
     * 保留的是「header 换边」，它仍然是一个真实的、主题能改的放置。
     */
    regions: {
      graph: { header: { mode: 'float', edge: 'top', gap: 6, radius: 8, reserveSpace: true } },
    },
  },
  /*
   * 通道 ③ 的活样例：给节点加一道顶边高光。
   * 这段会被 scopeCss 自动挂到 html[data-np-theme="midnight"] 之下。
   */
  css: `
.np-node::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 1px;
  border-radius: inherit;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
  pointer-events: none;
}
.np-area__header {
  background: linear-gradient(180deg, rgba(90,169,255,0.06), transparent);
}
`,
}

/* ══════════════════════════════════════════════════════════════════
   3. nord —— 冷调 · 描边
   ══════════════════════════════════════════════════════════════════ */

const nord: ThemeDocument = {
  id: 'nord',
  name: '北境',
  hint: 'Nord 冷调，描边节点，方形端口，克制',
  scheme: 'dark',
  palette: {
    accent: '#88c0d0',
    danger: '#bf616a',
    sockets: {
      number: '#a3adc2',
      text: '#81a1c1',
      boolean: '#ebcb8b',
      color: '#b9d47f',
      datetime: '#b48ead',
      rich: '#d08770',
      list: '#8fbcbb',
      page: '#7d8fc4',
      /*
       * ⚠️ 这里原来和 `color` 都是 `#a3be8c` —— `verify:theme` 的
       * 「端口色不许撞」当场抓出来的。撞色的后果不是难看，
       * 是**语义被破坏**：两个不同类型的端口长得一样，
       * 用户会以为它们能连。
       */
      component: '#8fd18f',
      any: '#8b95a8',
    },
    categories: {
      document: '#4c6a92',
      page: '#506d99',
      data: '#4c566a',
      function: '#3f6f70',
      logic: '#3f6f70',
      boolean: '#7d6a3c',
      text: '#4a6f96',
      number: '#566072',
      color: '#5f7a4a',
      time: '#6a5570',
      list: '#3f6f6d',
      convert: '#5a5a8a',
      component: '#4f7350',
      container: '#5f5470',
      utility: '#434c5e',
    },
    severity: { error: '#bf616a', warning: '#ebcb8b', info: '#81a1c1', success: '#a3be8c' },
  },
  tokens: {
    'surface-0': '#2e3440',
    'surface-bar': '#333b4a',
    'surface-bar-soft': '#2b313c',
    'surface-2': 'rgba(59, 66, 82, 0.96)',
    'surface-popup': 'rgba(59, 66, 82, 0.98)',
    'node-bg-top': '#3b4252',
    'node-bg-bottom': '#343b49',
    'border-soft': 'rgba(216, 222, 233, 0.12)',
    'border-strong': 'rgba(216, 222, 233, 0.22)',
  },
  recipes: {
    shell: 'layered',
    node: 'outline',
    header: 'underline',
    row: 'divided',
    widget: 'outline',
    socket: 'square',
    state: 'thick',
    popup: 'solid',
  },
  layout: {
    density: 'normal',
    /* ⚠️ 这里原来是「工具栏横排贴底」—— 工具栏已删，整条去掉 */
  },
}

/* ══════════════════════════════════════════════════════════════════
   4. neon —— 近黑 · 霓虹
   ══════════════════════════════════════════════════════════════════ */

const neon: ThemeDocument = {
  id: 'neon',
  name: '霓虹',
  hint: '近黑底 + 冷光强调色，菱形端口，发光选中',
  scheme: 'dark',
  palette: {
    accent: '#00e5ff',
    danger: '#ff3b6b',
    sockets: {
      number: '#8b95a3',
      text: '#00e5ff',
      boolean: '#ffe066',
      color: '#b6ff3b',
      datetime: '#c77dff',
      rich: '#ff8a3d',
      list: '#00ffc2',
      page: '#7b7bff',
      component: '#5cff8f',
      any: '#6b7280',
    },
    categories: {
      document: '#2f4fa8',
      page: '#3a5bb5',
      data: '#3a4250',
      function: '#0f7a8a',
      logic: '#0f7a8a',
      boolean: '#8a7418',
      text: '#0d6ea8',
      number: '#4a4a70',
      color: '#5f8a1a',
      time: '#7a3aa8',
      list: '#0f8a7a',
      convert: '#5a3ac8',
      component: '#1f8a4a',
      container: '#7a2f8a',
      utility: '#2a2f38',
    },
    severity: { error: '#ff3b6b', warning: '#ffe066', info: '#00e5ff', success: '#5cff8f' },
  },
  tokens: {
    'surface-0': '#05060a',
    'surface-bar': '#0a0c12',
    'surface-bar-soft': '#070910',
    'surface-2': 'rgba(12, 15, 22, 0.9)',
    'surface-popup': 'rgba(10, 13, 20, 0.95)',
    'node-bg-top': '#0e1118',
    'node-bg-bottom': '#0a0c12',
    'border-soft': 'rgba(0, 229, 255, 0.14)',
    'border-strong': 'rgba(0, 229, 255, 0.26)',
  },
  recipes: {
    shell: 'flat',
    node: 'neon',
    header: 'accent-bar',
    row: 'plain',
    widget: 'filled',
    socket: 'diamond',
    state: 'glow',
    popup: 'glass',
  },
  layout: { density: 'compact' },
  css: `
/* 强调色的一条细扫描光 —— 只在聚焦的窗口上出现 */
.np-area.is-focused::after {
  box-shadow: inset 0 0 0 1px var(--np-accent-soft), inset 0 0 24px -12px var(--np-accent);
}
`,
}

/* ══════════════════════════════════════════════════════════════════
   5. paper —— 暖白 · 柔和（浅色）
   ══════════════════════════════════════════════════════════════════ */

const paper: ThemeDocument = {
  id: 'paper',
  name: '纸白',
  hint: '暖白浅色，柔和控件，长时间看不累',
  scheme: 'light',
  palette: {
    accent: '#b56a10',
    danger: '#c0392b',
    sockets: {
      number: '#7d7869',
      text: '#2f6f9e',
      boolean: '#9a7418',
      color: '#6d8318',
      datetime: '#7a4aa8',
      rich: '#b85c1a',
      list: '#1f8a80',
      page: '#4a4aa8',
      component: '#3f8240',
      any: '#8d8778',
    },
    /*
     * ⚠️ 浅色主题的分类色**故意也偏深**：节点头上的文字是浅色的，
     * 分类色一淡，白字就糊了（这正是「移动端浅色主题没有正确应用」
     * 那次真机反馈的同类问题）。
     */
    categories: {
      document: '#41548a',
      page: '#4a6090',
      data: '#5c5c5c',
      function: '#376a6a',
      logic: '#376a6a',
      boolean: '#7a6428',
      text: '#3f6a90',
      number: '#585866',
      color: '#66702a',
      time: '#6a4478',
      list: '#2f7a72',
      convert: '#54468a',
      component: '#437a43',
      container: '#665378',
      utility: '#4a4a4a',
    },
    severity: { error: '#c0392b', warning: '#a8791a', info: '#2f6f9e', success: '#3f8240' },
  },
  tokens: {
    'node-bg-top': '#ffffff',
    'node-bg-bottom': '#f2efe8',
    'node-border': 'rgba(0, 0, 0, 0.16)',
    'node-border-selected': '#2b2a26',
    'node-shadow': '0 2px 6px rgba(90, 80, 60, 0.16)',
  },
  recipes: {
    shell: 'flat',
    node: 'gradient',
    header: 'gradient',
    row: 'striped',
    widget: 'soft',
    socket: 'circle',
    state: 'ring',
    popup: 'solid',
  },
  /*
   * ⚠️ **`normal` 而不是 `comfortable`**。
   *
   * `paper` 是旧「浅色主题」的迁移目标（`localStorage` 里存的 `'light'`
   * 会映射到它，见 `registry.ts`）。迁移**不该顺手把密度也改掉** ——
   * 用户只是点了个浅色，结果所有按钮和行高一起变大，观感是
   * 「整个界面都变了」。想要宽松密度的人可以显式换 `solar`（那套就是
   * `comfortable`，它同时充当这条通道的活样例）。
   */
  layout: {
    density: 'normal',
    /*
     * 通道 ⑥ 的活样例：**纸白是"静"的那一档**。
     * 它主打长时间看不累，动效也收敛到最低 —— 想验"动效档位真的生效了"，
     * 就切到它：窗口开合 / 面板滑入的手感会和别的主题明显不同。
     */
    motion: 'off',
  },
}

/* ══════════════════════════════════════════════════════════════════
   6. solar —— 奶油 · 琥珀（浅色）
   ══════════════════════════════════════════════════════════════════ */

const solar: ThemeDocument = {
  id: 'solar',
  name: '暖阳',
  hint: '奶油底 + 琥珀强调，圆润浮起的卡片感',
  scheme: 'light',
  palette: {
    accent: '#d97706',
    danger: '#dc2626',
    sockets: {
      number: '#8a8172',
      text: '#2b7bb9',
      boolean: '#a16207',
      color: '#65830f',
      datetime: '#7c3aed',
      rich: '#ea580c',
      list: '#0d9488',
      page: '#4f46e5',
      component: '#16a34a',
      any: '#9c937f',
    },
    categories: {
      document: '#4a5d99',
      page: '#55689e',
      data: '#6b625a',
      function: '#3a7a72',
      logic: '#3a7a72',
      boolean: '#8a6a1a',
      text: '#3a72a8',
      number: '#6a5f70',
      color: '#6f7a20',
      time: '#70489a',
      list: '#2f8578',
      convert: '#5a4aa0',
      component: '#46873f',
      container: '#6d5588',
      utility: '#544c44',
    },
    severity: { error: '#dc2626', warning: '#b45309', info: '#2b7bb9', success: '#16a34a' },
  },
  tokens: {
    'surface-0': '#fdf6e9',
    'surface-bar': '#fffaf0',
    'surface-bar-soft': '#fbf3e4',
    'node-bg-top': '#fffdf8',
    'node-bg-bottom': '#f7efe1',
    'node-border': 'rgba(120, 90, 40, 0.18)',
    'node-border-selected': '#3a2f22',
    'node-shadow': '0 3px 10px rgba(140, 100, 40, 0.14)',
    'radius-l': '12px',
    'node-radius': '10px',
  },
  recipes: {
    shell: 'layered',
    node: 'elevated',
    header: 'accent-bar',
    row: 'comfortable',
    widget: 'soft',
    socket: 'ring',
    state: 'shadow',
    popup: 'outline',
  },
  /*
   * 通道 ⑤ 的第二个样例：**工具栏停到右边**。
   * 换边是纯 flex 顺序，DOM 结构不变 —— 所以切主题不会重建 Area，
   * VueFlow 的视口和选中都还在。
   */
  layout: {
    density: 'comfortable',
    /* ⚠️ 这里原来是「工具栏贴右」—— 工具栏已删，整条去掉 */
  },
}

/**
 * 全部内置主题。
 *
 * ⚠️ **第一份是默认主题**（`graphite`）—— `registry.ts` 依赖这个顺序。
 */
export const BUILTIN_THEMES: readonly ThemeDocument[] = [
  graphite,
  midnight,
  nord,
  neon,
  paper,
  solar,
]

/** 默认主题 id */
export const DEFAULT_THEME_ID = BUILTIN_THEMES[0]!.id
